<template>
<ValidationObserver ref="gemaTas2026" v-slot="{ passes }">
    <BaseDialog
        :showDialog="showDialog"
        maxDialogWidth="800"
        maxCardHeight="900"
        headline="lang_LABEL_SALES_DATA_CAPTION_REPORT_GEMA_TAS_2026"
        :close="closeDialog"
        persistent
        :alvqa="alvqa"
    >
        <template v-slot:dialogText>
            <v-container fluid>
                <!-- FROM / TO MONTH -->
                <v-row>
                    <v-col>
                        <v-menu
                            ref="fromMonthMenu"
                            min-width="auto"
                            offset-y
                            :close-on-content-click="false"
                            :return-value.sync="fromDate"
                            v-model="fromMonthMenu"
                        >
                            <template v-slot:activator="{ on }">
                                <ValidationProvider name="fromDate" rules="required" v-slot="{ errors }" slim>
                                    <v-text-field
                                        v-on="on"
                                        :value="formattedFrom"
                                        :label="$t('lang_LABEL_STATISTICS_CAPTION_FROM_MONTH')"
                                        @click:clear="fromDate = null"
                                        readonly
                                        clearable
                                        :error-messages="errors"
                                        :alvqa="alvqa + '-textfield-from-month'"
                                    />
                                </ValidationProvider>
                            </template>
                            <v-date-picker
                                v-model="fromDate"
                                :locale="$i18n.locale"
                                color="buttonStandard"
                                no-title
                                type="month"
                                :alvqa="alvqa + '-datepicker-from-month'"
                            >
                                <v-spacer />
                                <BaseButton label="lang_GENERAL_BUTTON_CANCEL" @click="fromMonthMenu = false" :alvqa="alvqa + '-from-cancel'" />
                                <BaseButton label="lang_GENERAL_BUTTON_OK" @click="$refs.fromMonthMenu.save(fromDate)" :alvqa="alvqa + '-from-ok'" />
                            </v-date-picker>
                        </v-menu>
                    </v-col>

                    <v-col>
                        <v-menu
                            ref="toMonthMenu"
                            min-width="auto"
                            offset-y
                            :close-on-content-click="false"
                            :return-value.sync="toDate"
                            v-model="toMonthMenu"
                        >
                            <template v-slot:activator="{ on }">
                                <ValidationProvider name="toDate" rules="required" v-slot="{ errors }" slim>
                                    <v-text-field
                                        v-on="on"
                                        :value="formattedTo"
                                        :label="$t('lang_LABEL_STATISTICS_CAPTION_TO_MONTH')"
                                        @click:clear="toDate = null"
                                        readonly
                                        clearable
                                        :error-messages="errors"
                                        :alvqa="alvqa + '-textfield-to-month'"
                                    />
                                </ValidationProvider>
                            </template>
                            <v-date-picker
                                v-model="toDate"
                                :locale="$i18n.locale"
                                color="buttonStandard"
                                no-title
                                type="month"
                                :alvqa="alvqa + '-datepicker-to-month'"
                            >
                                <v-spacer />
                                <BaseButton label="lang_GENERAL_BUTTON_CANCEL" @click="toMonthMenu = false" :alvqa="alvqa + '-to-cancel'" />
                                <BaseButton label="lang_GENERAL_BUTTON_OK" @click="$refs.toMonthMenu.save(toDate)" :alvqa="alvqa + '-to-ok'" />
                            </v-date-picker>
                        </v-menu>
                    </v-col>
                </v-row>

                <!-- HAP MODE / PRICE TYPE -->
                <v-row>
                    <v-col>
                        <BaseEditPageDropdown
                            :context="context"
                            field="HapMode"
                            :items="hapModeOptions"
                            :label="$t('lang_LABEL_SALES_DATA_CAPTION_GEMA_TAS_HAP_MODE')"
                            :clearable="false"
                            rules="required"
                            :withMargin="false"
                            :alvqa="alvqa + '-dropdown-hap-mode'"
                        />
                    </v-col>
                    <v-col>
                        <BaseEditPageDropdown
                            :context="context"
                            field="PriceType"
                            :items="priceTypeOptions"
                            :label="$t('lang_LABEL_SALES_DATA_CAPTION_GEMA_TAS_PRICE_TYPE')"
                            :clearable="false"
                            rules="required"
                            :withMargin="false"
                            :alvqa="alvqa + '-dropdown-price-type'"
                        />
                    </v-col>
                </v-row>

                <!-- PAUSCHALRETOUREN / EXCLUDED COUNTRIES -->
                <v-row>
                    <v-col cols="4">
                        <BaseEditPageTextField
                            :context="context"
                            field="Pauschalretouren"
                            type="number"
                            :clearable="false"
                            :label="$t('lang_LABEL_SALES_DATA_CAPTION_GEMA_TAS_PAUSCHALRETOUREN')"
                            suffix="%"
                            rules="required|min_value:0|max_value:100"
                            :withMargin="false"
                            :alvqa="alvqa + '-textfield-pauschalretouren'"
                        />
                    </v-col>
                    <v-col cols="8">
                        <BaseEditPageTextField
                            :context="context"
                            field="ExcludedCountries"
                            :clearable="false"
                            :label="$t('lang_LABEL_SALES_DATA_CAPTION_GEMA_TAS_EXCLUDED_COUNTRIES')"
                            :withMargin="false"
                            :alvqa="alvqa + '-textfield-excluded-countries'"
                        />
                    </v-col>
                </v-row>
            </v-container>
        </template>

        <template v-slot:dialogActions>
            <v-container fluid>
                <v-row>
                    <v-col cols="12" class="text-right d-flex justify-end align-center flex-wrap">
                        <span v-if="aiAnalysisLink" class="pr-3">
                            <v-icon small color="iconStandard" class="mr-1">auto_fix_high</v-icon>
                            <a :href="aiAnalysisLink" target="_blank">{{ $t('lang_LABEL_SALES_DATA_CAPTION_GEMA_TAS_AI_DOWNLOAD') }}</a>
                        </span>
                        <span v-if="showAiButton && !aiAnalysisLink && !isAnalyzing" class="pr-3">
                            <a
                                href="#"
                                @click.prevent="analyze"
                                :alvqa="alvqa + '-link-ai-analyze'"
                            >
                                <v-icon small color="iconStandard" class="mr-1">auto_fix_high</v-icon>
                                {{ $t('lang_LABEL_SALES_DATA_CAPTION_GEMA_TAS_AI_ANALYZE') }}
                            </a>
                        </span>
                        <span v-if="isAnalyzing" class="pr-3">
                            <v-icon small color="iconStandard" class="mr-1">auto_fix_high</v-icon>
                            <v-progress-circular
                                size="16"
                                width="2"
                                color="dialogProgressbar"
                                indeterminate
                                :alvqa="alvqa + '-ai-progress'"
                            />
                        </span>
                        <span v-if="reportLink && (showAiButton || aiAnalysisLink || isAnalyzing)" class="pr-3 grey--text">|</span>
                        <span v-if="reportLink" class="pr-3">
                            <a :href="reportLink" target="_blank">{{ $t('lang_GENERIC_CAPTION_CLICK_HERE_TO_DOWNLOAD') }}</a>
                        </span>
                        <span v-if="loading" class="pr-3">
                            <v-progress-circular
                                size="16"
                                width="2"
                                color="dialogProgressbar"
                                indeterminate
                                :alvqa="alvqa + '-progress'"
                            />
                        </span>
                        <BaseButton
                            label="lang_BUTTON_START"
                            @click="passes(start)"
                            :disabled="loading"
                            :alvqa="alvqa + '-button-start'"
                        />
                    </v-col>
                </v-row>
            </v-container>
        </template>
    </BaseDialog>
</ValidationObserver>
</template>

<script>
import BaseDialog from '@/components/_base/BaseDialog'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'
import BaseEditPageTextField from '@/components/_editForm/BaseEditPageTextField'

export default {
    name: 'LabelSalesDataGemaTas2026Dialog',
    components: { BaseDialog, BaseButton, BaseEditPageDropdown, BaseEditPageTextField },
    data() {
        return {
            alvqa: 'label-sales-data-gema-tas-2026-dialog',
            context: 'GemaTas2026Dialog',
            fromMonthMenu: false,
            toMonthMenu: false,
        }
    },
    computed: {
        showDialog: {
            get() { return this.$store.getters.getShowGemaTas2026Dialog },
            set(val) { this.$store.commit('setShowGemaTas2026Dialog', val) }
        },
        fromDate: {
            get() { return this.$store.getters.getGemaTas2026DialogDetailsFromDate },
            set(val) { this.$store.commit('setGemaTas2026DialogDetailsFromDate', val) }
        },
        toDate: {
            get() { return this.$store.getters.getGemaTas2026DialogDetailsToDate },
            set(val) { this.$store.commit('setGemaTas2026DialogDetailsToDate', val) }
        },
        reportLink() {
            return this.$store.getters.getGemaTas2026DialogReportLink
        },
        aiAnalysisLink() {
            return this.$store.getters.getGemaTas2026DialogAiAnalysisLink
        },
        isAnalyzing() {
            return this.$store.getters.getGemaTas2026DialogIsAnalyzing
        },
        showAiButton() {
            return Boolean(this.reportLink)
        },
        loading() { return this.$store.getters.getIsLoading },

        formattedFrom() {
            return this.fromDate ? this.$d(new Date(this.fromDate + '-01'), 'alvMonthYearFormat') : ''
        },
        formattedTo() {
            return this.toDate ? this.$d(new Date(this.toDate + '-01'), 'alvMonthYearFormat') : ''
        },

        hapModeOptions() {
            return [
                { text: this.$t('lang_LABEL_SALES_DATA_CAPTION_GEMA_TAS_HAP_MODE_AVERAGE'), value: 'average' },
                { text: this.$t('lang_LABEL_SALES_DATA_CAPTION_GEMA_TAS_HAP_MODE_HIGHEST'), value: 'highest' },
            ]
        },
        priceTypeOptions() {
            return [
                { text: 'HAP', value: 'HAP' },
                { text: 'EVP', value: 'EVP' },
            ]
        },
    },
    methods: {
        closeDialog() {
            this.showDialog = false
            this.$store.commit('setSelectedFileReport', [])
            this.$store.commit('setGemaTas2026DialogReportLink', null)
            this.$store.commit('setGemaTas2026DialogReportStamp', null)
            this.$store.commit('setGemaTas2026DialogAiAnalysisLink', null)
        },
        start() {
            this.$store.dispatch('createLabelGemaTas2026Report')
        },
        analyze() {
            if (this.isAnalyzing) return
            this.$store.dispatch('analyzeLabelGemaTas2026Report')
        }
    }
}
</script>
